<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, type Ref, ref, watch } from "vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import { NotifType, useStoreApp } from "~/stores/storeApp";
import { useStoreMarketplace } from "~/stores/storeMarketplace";
import {
  AuctionStatus,
  AuctionUpdate,
  CardType,
  MarketType,
  NftEquipment,
  NftSkill,
  SocketEventType,
} from "fungi-types";
import { useCardDetails } from "../card-details/use-card-details";
import CardDataFooterScore from "../card/CardDataFooterScore.vue";
import CardDataFooterSkeleton from "../card/CardDataFooterSkeleton.vue";
import CardDataFooterPrice from "../card/CardDataFooterPrice.vue";

import NftSkillCard from "./NftSkill.vue";
import { useStoreDeck } from "~/stores/storeDeck";
import { useI18n } from "vue-i18n";
import socketService from "~/services/socketService";

import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
import { Currency } from "fungi-types";
const web3AuthStore = useWeb3AuthStore();

dayjs.extend(utc);
dayjs.extend(duration);

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    isInteractive?: boolean;
    item: NftSkill | NftEquipment;
    showCardDetails?: boolean;
    currentlyAvailable?: number;
    useCurrency?: Currency;
    showXpBar?: boolean;
    isSecondary?: boolean;
    type?: "black" | "gray";
    locked?: boolean;
    showBidOrBuyBtn?: boolean;
  }>(),
  {
    showCardDetails: true,
    isInteractive: true,
    showXpBar: false,
    currentlyAvailable: -1,
    isSecondary: false,
    activeBlur: false,
    locked: false,
    showBidOrBuyBtn: true,
  }
);

const emits = defineEmits<{
  (event: "buy", data: { item: NftSkill; loading: Ref }): void;
  (event: "card-click", data: { card: NftSkill | NftEquipment }): void;
}>();

const timeOver = ref(false);
const loading = ref(false);

const auctionStatus = ref<AuctionStatus | undefined>(props.item.auction?.status);

const handleTimeoutEvent = () => {
  timeOver.value = true;
};

function updateAuction(data: AuctionUpdate) {
  if (data.auction.cuid === props.item.auction?.cuid) {
    const end = new Date(data.auction.endedAt);

    // check if the auction has ended
    timeOver.value = end.getTime() <= new Date().getTime();

    auctionStatus.value = data.auction.status;
  }
}

function bidOnCard() {
  useStoreMarketplace().selectedItem = props.item;
  useStoreApp().setBidModalState(true);
}

const isCardOwner = computed(() => {
  const walletAddresses = [web3AuthStore.walletAddress];
  if (web3AuthStore.smartWalletAddress) walletAddresses.push(web3AuthStore.smartWalletAddress);
  return walletAddresses.some(
    (address) => address.toLowerCase() === props.item.owner?.toLowerCase()
  );
});

function buyItem() {
  useStoreMarketplace().selectedItem = props.item;
  useStoreApp().setBidModalState(true);
}

onMounted(() => {
  socketService.on(SocketEventType.AUCTION_UPDATED, updateAuction);
});

onBeforeUnmount(() => {
  socketService.off(SocketEventType.AUCTION_UPDATED, updateAuction);
});

watch(
  () => [props.item, auctionStatus],
  () => {
    if (
      props.item?.auction?.status === AuctionStatus.ENDED ||
      auctionStatus.value === AuctionStatus.ENDED
    ) {
      loading.value = false;
    }
  }
);

const cardDetails = useCardDetails();
const navigateToCardDetails = (cardToShowTheDetailsFor: NftSkill) => {
  cardDetails.loadCard(cardToShowTheDetailsFor.tokenId, props.item as NftSkill);
  cardDetails.showModal = true;
};

const handleClickOnCard = () => {
  if (props.showCardDetails && props.item.cardType === CardType.NftSkills) {
    navigateToCardDetails(props.item as NftSkill);
  } else {
    emits("card-click", { card: props.item });
  }
};

const xpCalculation = computed(() => {
  let xp = 0;

  if ("xp" in props.item) xp = props.item.xp;
  else return xp;

  if (props.item?.auction?.marketType === MarketType.SECONDARY && xp > 0) {
    return xp / 2;
  }

  return xp;
});

const handleList = () => {
  const storeDeck = useStoreDeck();
  const storeApp = useStoreApp();
  storeDeck.selected = props.item as NftSkill;
  storeApp.showListModal = true;
};

const handleUnlist = async () => {
  loading.value = true;
  const storeDeck = useStoreDeck();
  const storeApp = useStoreApp();
  if (props.item.auction) {
    await storeDeck.unList(props.item.auction.cuid, props.item.tokenId, props.item.cardType);
    storeApp.displayNotification(NotifType.UNLIST_SUCCESS, props.item as NftSkill);
  } else {
    storeApp.displayNotification(
      NotifType.UNLIST_FAILED,
      props.item as NftSkill,
      t("modals.list.messages.listing-not-found")
    );
  }
  loading.value = false;
};
</script>

<template>
  <NftSkillCard class="w-fit relative" :nft="item" @click="handleClickOnCard">
    <template #footer-data>
      <CardDataFooterSkeleton :type="type" v-if="showXpBar && item.cardType === CardType.NftSkills">
        <XpLevelBar :xp="xpCalculation" />
      </CardDataFooterSkeleton>
      <CardDataFooterSkeleton :type="type">
        <CardDataFooterScore
          v-if="(item as NftSkill).skill"
          :l10="(item as NftSkill).score"
          :l5="(item as NftSkill).l5"
          :court="(item as NftSkill).courtType"
          :skill="(item as NftSkill).skill"
          :scarcity="(item as NftSkill).scarcity"
          :season="(item as NftSkill).season"
          :entries="(item as NftSkill).entries"
          :xp="xpCalculation"
        />

        <CardDataFooterEquipment
          v-else-if="item.cardType === CardType.NftEquipment"
          :bonus="(item as NftEquipment).bonus"
          :level="(item as NftEquipment).level"
          :edition="(item as NftEquipment).edition"
          :brand="(item as NftEquipment).brand"
          :color="(item as NftEquipment).color"
        />

        <CardDataFooterPrice
          v-if="item.auction?.cuid"
          :auction="item.auction"
          :useCurrency="useCurrency"
          :scarcity="(item as NftSkill).scarcity"
          :ownedBy="item.ownedBy"
          @time-out="handleTimeoutEvent"
        />
      </CardDataFooterSkeleton>
    </template>

    <template #cta v-if="showBidOrBuyBtn">
      <CardDataFooterCtaListCard
        v-if="isCardOwner"
        :auctionStatus="item.auction?.status"
        :loading="loading"
        :saleLocked="item.saleUnlocksAt"
        @list="handleList"
        @unlist="handleUnlist"
        :locked="locked"
      />
      <cardDataFooterCtaBuyBidCard
        v-else-if="item.auction"
        :item="item.auction"
        :is-interactive="isInteractive"
        :is-owner="isCardOwner"
        :loading="loading"
        :time-over="timeOver"
        @buy="buyItem()"
        @bid="bidOnCard"
        :locked="locked"
      />
    </template>
  </NftSkillCard>
</template>

<style scoped lang="scss">
.winner {
  animation: winner 0.6s cubic-bezier(0.87, 0, 0.13, 1);
}

@keyframes winner {
  from {
    scale: 1.66;
    filter: drop-shadow(0 0 4em #ffffff);
  }
  to {
    scale: 1;
    filter: drop-shadow(0 0 0em #ffffff);
  }
}
</style>
